import apiClient from '@/api/client'
import type {
  AuthTokens,
  LoginCredentials,
  User,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
} from '@/types'

// Map snake_case API user to frontend User type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapUser(raw: any): User {
  const fullName: string = raw.full_name || raw.fullName || ''
  const parts = fullName.trim().split(' ')

  // Role: API returns roles as string array; build a Role object from the first entry
  const roleName: string = raw.roles?.[0] ?? raw.role ?? 'bde'
  const role: User['role'] = typeof roleName === 'object' && roleName !== null
    ? roleName
    : {
        id: roleName,
        name: roleName,
        isDefault: false,
        isSystem: true,
        permissions: raw.permissions
          ? raw.permissions.map((p: string) => {
              const [resource, action] = p.split(':')
              return { id: p, name: p, resource: resource ?? p, action: action ?? '*', createdAt: '', updatedAt: '' }
            })
          : [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

  return {
    id: raw.id,
    email: raw.email,
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
    fullName,
    phone: raw.phone ?? undefined,
    avatar: raw.avatar_url ?? raw.avatarUrl ?? undefined,
    isActive: raw.is_active !== false && raw.isActive !== false,
    isEmailVerified: raw.email_verified ?? raw.emailVerified ?? raw.isEmailVerified ?? true,
    role,
    tenantId: String(raw.tenant_id ?? raw.tenantId ?? ''),
    lastLoginAt: raw.last_login ?? raw.lastLoginAt ?? undefined,
    createdAt: raw.created_at ?? raw.createdAt ?? new Date().toISOString(),
    updatedAt: raw.updated_at ?? raw.updatedAt ?? new Date().toISOString(),
  }
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<{ user: User; tokens: AuthTokens }> => {
    const response = await apiClient.post('/auth/login', {
      email: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberMe ?? false,
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = response.data as any
    return {
      user: mapUser(raw.user),
      tokens: {
        accessToken: raw.accessToken ?? raw.access_token,
        refreshToken: raw.refreshToken ?? raw.refresh_token,
        expiresIn: raw.expiresIn ?? raw.expires_in ?? 900,
        tokenType: 'Bearer',
      },
    }
  },

  refresh: async (refreshToken?: string): Promise<AuthTokens> => {
    const body = refreshToken ? { refresh_token: refreshToken } : {}
    const response = await apiClient.post('/auth/refresh', body)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = response.data as any
    return {
      accessToken: raw.accessToken ?? raw.access_token,
      refreshToken: raw.refreshToken ?? raw.refresh_token,
      expiresIn: raw.expiresIn ?? raw.expires_in ?? 900,
      tokenType: 'Bearer',
    }
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout')
  },

  getMe: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me')
    return mapUser(response.data)
  },

  /**
   * Change the current user's password
   */
  changePassword: async (data: ChangePasswordRequest): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/change-password', data)
    return response.data
  },

  /**
   * Send a forgot-password email
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/forgot-password', data)
    return response.data
  },

  /**
   * Reset password with a valid token
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', data)
    return response.data
  },

  /**
   * Verify an email with a verification token
   */
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/verify-email', { token })
    return response.data
  },

  /**
   * Resend email verification
   */
  resendVerification: async (): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/resend-verification')
    return response.data
  },
}
